import { Eye, EyeOff } from 'lucide-react';
import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type InputHTMLAttributes,
  type ReactNode,
  type Ref,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { cn } from '../../lib/cn';

const control =
  'w-full rounded-input border bg-white px-3 text-base text-ink placeholder:text-slate focus:outline-none focus:ring-2 focus:ring-brand-light/40 focus:border-brand-light disabled:bg-mist';

interface FieldShellProps {
  id: string;
  label: string;
  error?: string;
  hint?: ReactNode;
  children: ReactNode;
  required?: boolean;
}

function FieldShell({ id, label, error, hint, children, required }: FieldShellProps) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium text-ink">
        {label}
        {required && <span className="text-slate"> (required)</span>}
      </label>
      {children}
      {hint && !error && (
        <p id={`${id}-hint`} className="text-xs text-slate">
          {hint}
        </p>
      )}
      {error && (
        <p id={`${id}-error`} className="text-sm text-danger-700" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

function describedBy(id: string, error?: string, hint?: ReactNode) {
  return error ? `${id}-error` : hint ? `${id}-hint` : undefined;
}

interface CommonProps {
  label: string;
  error?: string;
  hint?: ReactNode;
}

export const TextField = forwardRef<
  HTMLInputElement,
  CommonProps & InputHTMLAttributes<HTMLInputElement>
>(function TextField({ label, error, hint, id, className, required, ...props }, ref) {
  const autoId = useId();
  const fieldId = id ?? autoId;
  return (
    <FieldShell id={fieldId} label={label} error={error} hint={hint} required={required}>
      <input
        ref={ref}
        id={fieldId}
        aria-invalid={!!error || undefined}
        aria-describedby={describedBy(fieldId, error, hint)}
        aria-required={required || undefined}
        className={cn(control, 'h-10', error ? 'border-danger-700' : 'border-stone', className)}
        {...props}
      />
    </FieldShell>
  );
});

/**
 * Password input with a show/hide toggle inside the box. The toggle never submits the form,
 * and the field goes back to hidden whenever its form is submitted.
 */
export const PasswordField = forwardRef<
  HTMLInputElement,
  CommonProps & Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>
>(function PasswordField({ label, error, hint, id, className, required, ...props }, ref) {
  const autoId = useId();
  const fieldId = id ?? autoId;
  const [visible, setVisible] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const setRefs = useCallback(
    (node: HTMLInputElement | null) => {
      inputRef.current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref) ref.current = node;
    },
    [ref],
  );
  useEffect(() => {
    const form = inputRef.current?.form;
    if (!form) return;
    const hide = () => setVisible(false);
    form.addEventListener('submit', hide);
    return () => form.removeEventListener('submit', hide);
  }, []);
  const Icon = visible ? EyeOff : Eye;
  return (
    <FieldShell id={fieldId} label={label} error={error} hint={hint} required={required}>
      <div className="relative">
        <input
          ref={setRefs}
          id={fieldId}
          type={visible ? 'text' : 'password'}
          aria-invalid={!!error || undefined}
          aria-describedby={describedBy(fieldId, error, hint)}
          aria-required={required || undefined}
          className={cn(
            control,
            'h-10 pr-11',
            error ? 'border-danger-700' : 'border-stone',
            className,
          )}
          {...props}
        />
        <button
          type="button"
          aria-label={visible ? `Hide ${label.toLowerCase()}` : `Show ${label.toLowerCase()}`}
          aria-pressed={visible}
          aria-controls={fieldId}
          onClick={() => setVisible((v) => !v)}
          className="absolute inset-y-0 right-0 flex w-11 items-center justify-center rounded-r-input text-slate hover:text-ink focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand-light"
        >
          <Icon className="size-5" aria-hidden />
        </button>
      </div>
    </FieldShell>
  );
});

export const TextAreaField = forwardRef<
  HTMLTextAreaElement,
  CommonProps & TextareaHTMLAttributes<HTMLTextAreaElement>
>(function TextAreaField({ label, error, hint, id, className, required, ...props }, ref) {
  const autoId = useId();
  const fieldId = id ?? autoId;
  return (
    <FieldShell id={fieldId} label={label} error={error} hint={hint} required={required}>
      <textarea
        ref={ref}
        id={fieldId}
        aria-invalid={!!error || undefined}
        aria-describedby={describedBy(fieldId, error, hint)}
        aria-required={required || undefined}
        className={cn(
          control,
          'min-h-28 py-2',
          error ? 'border-danger-700' : 'border-stone',
          className,
        )}
        {...props}
      />
    </FieldShell>
  );
});

export const SelectField = forwardRef<
  HTMLSelectElement,
  CommonProps & SelectHTMLAttributes<HTMLSelectElement>
>(function SelectField({ label, error, hint, id, className, required, children, ...props }, ref) {
  const autoId = useId();
  const fieldId = id ?? autoId;
  return (
    <FieldShell id={fieldId} label={label} error={error} hint={hint} required={required}>
      <select
        ref={ref}
        id={fieldId}
        aria-invalid={!!error || undefined}
        aria-describedby={describedBy(fieldId, error, hint)}
        aria-required={required || undefined}
        className={cn(control, 'h-10', error ? 'border-danger-700' : 'border-stone', className)}
        {...props}
      >
        {children}
      </select>
    </FieldShell>
  );
});

type InputProps = InputHTMLAttributes<HTMLInputElement> & { ref?: Ref<HTMLInputElement> };

interface PhoneFieldProps {
  label: string;
  required?: boolean;
  hint?: ReactNode;
  /** Props for the country-code input (e.g. `register('countryCode')`). */
  codeInput: InputProps;
  /** Props for the national-number input (e.g. `register('phone')`). */
  numberInput: InputProps;
  codeError?: string;
  numberError?: string;
}

/**
 * Country code and number as one control on a single row at every width. The visible label
 * names the number input; the code input has its own accessible name.
 */
export function PhoneField({
  label,
  required,
  hint,
  codeInput,
  numberInput,
  codeError,
  numberError,
}: PhoneFieldProps) {
  const id = useId();
  const codeId = `${id}-code`;
  const error = numberError ?? codeError;
  const describedById = error ? `${id}-error` : hint ? `${id}-hint` : undefined;
  return (
    <FieldShell id={id} label={label} error={error} hint={hint} required={required}>
      <div
        className={cn(
          'flex h-10 items-stretch overflow-hidden rounded-input border bg-white focus-within:border-brand-light focus-within:ring-2 focus-within:ring-brand-light/40',
          error ? 'border-danger-700' : 'border-stone',
        )}
      >
        <input
          id={codeId}
          aria-label="Country code"
          autoComplete="tel-country-code"
          inputMode="tel"
          aria-invalid={!!codeError || undefined}
          aria-describedby={codeError ? `${id}-error` : undefined}
          aria-required={required || undefined}
          className="w-16 shrink-0 border-r border-stone bg-mist px-2 text-center text-base tabular-nums text-ink focus:outline-none"
          {...codeInput}
        />
        <input
          id={id}
          type="tel"
          autoComplete="tel-national"
          inputMode="tel"
          aria-invalid={!!numberError || undefined}
          aria-describedby={describedById}
          aria-required={required || undefined}
          className="min-w-0 flex-1 px-3 text-base tabular-nums text-ink placeholder:text-slate focus:outline-none"
          {...numberInput}
        />
      </div>
    </FieldShell>
  );
}

/** Bare input/select styles for filters and toolbars. */
export const controlClass = cn(control, 'h-10 border-stone');
